import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Lock, Mail, User } from 'lucide-react';
import { signIn } from '../lib/auth';
import toast from 'react-hot-toast';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  
  // Extract parameters from URL if present
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('return_to');
  const paymentComplete = searchParams.get('payment_complete') === 'true';

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      // Show a special loading toast for payment return users
      const loadingToastId = toast.loading(
        paymentComplete 
          ? 'Signing you in after payment...'
          : 'Signing in...'
      );
      
      await signIn({
        email: formData.email,
        password: formData.password,
      });
      
      // Clear the loading toast
      toast.dismiss(loadingToastId);

      // First check for return_to parameter in URL, then location state, then default to account
      let redirectTo;
      
      if (returnTo) {
        // If we have a return_to parameter, use it (it's already URI encoded)
        console.log('Redirecting to:', decodeURIComponent(returnTo));
        
        // Special success message for payment return users
        if (paymentComplete) {
          // Mark successful payment in localStorage for other components
          localStorage.setItem('subscription_success', 'true');
          localStorage.setItem('subscription_success_time', new Date().getTime().toString());
          
          toast.success('Payment confirmed! Your subscription is now active.', { duration: 5000 });
        } else {
          toast.success('Successfully logged in! Redirecting...');
        }
        
        // Short delay before redirect to show toast
        setTimeout(() => {
          window.location.href = decodeURIComponent(returnTo);
        }, 1000);
        
        return; // Exit early as we're handling redirect manually
      } else {
        // Fall back to location state or default
        redirectTo = location.state?.from?.pathname || '/account';
        navigate(redirectTo, { replace: true });
        toast.success('Successfully logged in!');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.message === 'Invalid login credentials') {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else if (err.message?.includes('Email not confirmed')) {
        setError('Please verify your email address before logging in.');
      } else {
        setError('An error occurred during login. Please try again.');
      }
      toast.error('Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Show success message if redirected from registration
  const registrationMessage = location.state?.message;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <User className="mx-auto h-12 w-12 text-rose-600" />
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="font-medium text-rose-600 hover:text-rose-500">
              Register here
            </Link>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-md sm:rounded-lg sm:px-10">
            {registrationMessage && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-600">{registrationMessage}</p>
              </div>
            )}
            
            {paymentComplete && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <h3 className="text-sm font-medium text-green-800 mb-1">Payment Successful!</h3>
                <p className="text-sm text-green-600">
                  Your payment has been processed successfully. Please sign in and click My Account to add your Listings.
                </p>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1 relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-rose-500 focus:border-rose-500"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  <Mail className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-rose-500 focus:border-rose-500"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <Lock className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-rose-600 focus:ring-rose-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link to="/reset-password" className="font-medium text-rose-600 hover:text-rose-500">
                    Forgot your password?
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 disabled:opacity-50"
                >
                  {isSubmitting ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;